import React from 'react';
import { Footer } from 'src/features/footer/Footer';
import {
  markAACChromeReady,
  markComponentStart,
} from 'src/commons/utils/logWithTakt';
import { Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import { initFooterState } from 'src/features/footer/store/slice';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { FooterMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { getChromeData } from 'src/commons/apis';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { ChromeFooterData } from '@amzn/aac-chrome-internal-api';
import { ErrorIdProvider } from '@amzn/storm-ui';

export default class AXChromeFooter extends HTMLElement {
  static observedAttributes = ['initChromeFooterConfig'];

  constructor() {
    super();
  }

  set initChromeFooterConfig(initChromeFooterConfig: ChromeFooterData) {
    if (initChromeFooterConfig) {
      this.updateFooterStore(initChromeFooterConfig);
    }
  }

  updateFooterStore(initChromeFooterConfig: ChromeFooterData) {
    store.dispatch(initFooterState(initChromeFooterConfig));
  }

  async _renderFooter() {
    if (window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__) {
      const hide = await window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__;
      if (hide) return;
    }

    markComponentStart(Components.FOOTER);
    // initChromeFooter is called at last among all the components
    // Marking AACChrome ready since all its dependent comopents are registered now
    markAACChromeReady();
    const endMarker = performanceMark('aac-chrome-ui-footer');

    const initFooterData = async () => {
      try {
        const { chromeFooterData } = await getChromeData();
        store.dispatch(initFooterState(chromeFooterData));
        FooterMetricPublisher.publishCounterMonitor('initFailure', 0);
      } catch (e: any) {
        FooterMetricPublisher.publishCounterMonitor('initFailure', 1);
        logger.error('failed to fetch chromeData:', e);
      }
    };

    if (isFromTrim() || isFromBtswWc()) {
      const trimFooterConfig: ChromeFooterData = this['initChromeFooterConfig'];
      this.updateFooterStore(trimFooterConfig);
    } else {
      initFooterData();
    }

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name="Footer">
                <Footer />
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderFooter();
  }
}

if (!customElements.get('ax-chrome-footer')) {
  customElements.define('ax-chrome-footer', AXChromeFooter);
}
